import { Filter, RefreshCw, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  hasActiveAffinityChartFilters,
  type AffinityChartFilterOption,
  type AffinityChartFilters,
  type AffinityChartSnapshot,
  type AffinityChartVisibleGraph,
} from "@/features/affinity-chart/affinity-chart.types";
import { Button } from "@/components/ui/button";

type AffinityChartToolbarProps = {
  filterOptions: AffinityChartFilterOption[];
  filters: AffinityChartFilters;
  onRefresh: () => void;
  onResetFilters: () => void;
  onResetLayout: () => void;
  onToggleType: (type: AffinityChartFilterOption["type"]) => void;
  snapshot: AffinityChartSnapshot;
  visibleGraph: AffinityChartVisibleGraph;
};

export function AffinityChartToolbar({
  filterOptions,
  filters,
  onRefresh,
  onResetFilters,
  onResetLayout,
  onToggleType,
  snapshot,
  visibleGraph,
}: AffinityChartToolbarProps) {
  const { t } = useTranslation();
  const hasActiveFilters = hasActiveAffinityChartFilters(filters, filterOptions);
  const totalNodes = snapshot.counts.entityNodes + snapshot.counts.loreNodes;

  return (
    <section className="space-y-5 border-b border-ink/10 pb-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("affinityChart.toolbar.badge")}
          </p>
          <div className="space-y-1">
            <h2 className="text-[2rem] leading-none text-foreground">{t("affinityChart.toolbar.title")}</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              {t("affinityChart.toolbar.summary", {
                totalEdges: snapshot.counts.edges,
                totalNodes,
                visibleEdges: visibleGraph.counts.visibleEdges,
                visibleNodes: visibleGraph.counts.visibleNodes,
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={onRefresh} type="button" variant="outline">
            <RefreshCw className="h-4 w-4" />
            {t("affinityChart.actions.refresh")}
          </Button>
          <Button onClick={onResetLayout} type="button" variant="outline">
            <RotateCcw className="h-4 w-4" />
            {t("affinityChart.actions.resetLayout")}
          </Button>
        </div>
      </div>

      {filterOptions.length > 0 ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <Filter className="h-4 w-4" />
              {t("affinityChart.toolbar.filters")}
            </div>
            {hasActiveFilters ? (
              <Button onClick={onResetFilters} size="sm" type="button" variant="ghost">
                {t("affinityChart.actions.clearFilters")}
              </Button>
            ) : null}
          </div>
          <div
            aria-label="Affinity chart node type filters"
            className="flex flex-wrap gap-2"
            role="group"
          >
            {filterOptions.map((option) => {
              const selected = filters.visibleTypes.includes(option.type);

              return (
                <Button
                  aria-pressed={selected}
                  key={option.type}
                  onClick={() => onToggleType(option.type)}
                  size="sm"
                  type="button"
                  variant={selected ? "default" : "outline"}
                >
                  {t(option.labelKey)}
                  <span className="text-xs opacity-80">({option.count})</span>
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}
